import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Event } from "./useEvents";

export const useCandidateEvents = (candidateId: string | undefined) => {
  return useQuery({
    queryKey: ['events', 'candidate', candidateId],
    queryFn: async (): Promise<Event[]> => {
      if (!candidateId) return [];

      const { data, error } = await supabase
        .from('events')
        .select('*')
        .contains('relations', { candidates: [candidateId] })
        .order('start_time', { ascending: true });

      if (error) {
        throw new Error(error.message);
      }

      // If we have events with assignees, fetch the assignee information separately
      if (data && data.length > 0) {
        const assigneeIds = data
          .filter(event => event.assigned_to)
          .map(event => event.assigned_to)
          .filter((id, index, arr) => arr.indexOf(id) === index); // Remove duplicates

        if (assigneeIds.length > 0) {
          try {
            const { data: assigneeData, error: assigneeError } = await supabase
              .rpc('get_multiple_users_display_info', {
                p_user_ids: assigneeIds
              });

            if (!assigneeError && assigneeData) {
              // Create a map of user_id to user info
              const assigneeMap = new Map(
                assigneeData.map(user => [user.user_id, user])
              );

              // Attach assignee info to events
              return data.map(event => ({
                ...event,
                assignee: event.assigned_to ? assigneeMap.get(event.assigned_to) : undefined
              }));
            }
          } catch (assigneeError) {
            console.warn('Failed to fetch assignee information:', assigneeError);
          }
        }
      }

      return data || [];
    },
    enabled: !!candidateId,
  });
};
